'use client'

import type { Asset, User } from '@/lib/types'
import { useCallback, useState } from 'react'
import { useUploadAssets } from '@/hooks/use-upload-assets'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import Image from 'next/image'
import { HeroPrompt } from './hero-prompt'
import { MAX_CONTENT_ASSETS } from '@/components/modals/upload-assets'
import { pushModal } from '@/components/modals'

export function Hero({ user }: { user: User }) {
  const [isDragging, setIsDragging] = useState(false)
  const [assets, setAssets] = useState<Asset[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const t = useTranslations('home_v3.hero')

  const modalt = useTranslations('modals')
  const { uploadAssets } = useUploadAssets({
    maxUploadAssets: 25,
  })

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    // Only set dragging to false if we're leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false)
    }
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)

      const files = Array.from(e.dataTransfer.files)

      if (files.length === 0) return

      // Check upload limits
      const currentContentAssets = assets.filter(
        (asset) => asset.source === 'upload' || asset.source === 'chat',
      )
      const currentCount = currentContentAssets.length
      const selectedCount = files.length
      const totalAfterUpload = currentCount + selectedCount

      if (totalAfterUpload > MAX_CONTENT_ASSETS) {
        const remaining = Math.max(0, MAX_CONTENT_ASSETS - currentCount)
        setErrorMessage(
          modalt('upload-assets.maximum_content_error', {
            MAX_CONTENT_ASSETS,
            remaining,
          }),
        )
        return
      }

      // Create temporary assets immediately to show files with shimmer
      const tempAssets: Asset[] = files.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`
        const isImage = file.type.startsWith('image/')
        const thumbnail = isImage ? URL.createObjectURL(file) : undefined

        let assetType: Asset['type'] = 'image'
        if (file.type.startsWith('video/')) assetType = 'video'
        else if (file.type.startsWith('audio/')) assetType = 'audio'
        else if (file.type.startsWith('application/pdf')) assetType = 'pdf'
        else if (file.type.startsWith('text/csv')) assetType = 'csv'

        return {
          source: 'chat' as const,
          type: assetType,
          link: `temp://${tempId}`,
          thumbnail,
          name: file.name,
          payload: { tempId, isUploading: true },
        }
      })

      // Add temporary assets immediately (user sees them right away with shimmer)
      setAssets((prev) => [...prev, ...tempAssets])

      try {
        // Upload files
        const result = await uploadAssets(files, {
          assetSource: 'chat',
          assetDir: 'user-assets',
        })

        if (!result.success) {
          toast.error(result.error, { duration: 2000 })
          // Remove temporary assets on error
          setAssets((prev) =>
            prev.filter((asset) => !asset.link?.startsWith('temp://')),
          )
          return
        }

        // Replace temporary assets with real ones
        setAssets((prev) => {
          // Remove temp assets
          const withoutTempAssets = prev.filter(
            (asset) => !asset.link?.startsWith('temp://'),
          )

          // Add real assets
          return [...withoutTempAssets, ...result.assets]
        })

        // Clean up object URLs
        tempAssets.forEach((asset) => {
          if (asset.thumbnail && asset.thumbnail.startsWith('blob:')) {
            URL.revokeObjectURL(asset.thumbnail)
          }
        })
      } catch (error) {
        // Remove temporary assets on error
        setAssets((prev) =>
          prev.filter((asset) => !asset.link?.startsWith('temp://')),
        )

        // Clean up object URLs on error
        tempAssets.forEach((asset) => {
          if (asset.thumbnail && asset.thumbnail.startsWith('blob:')) {
            URL.revokeObjectURL(asset.thumbnail)
          }
        })
        // console.error('Hero: Error uploading files:', error)
        setErrorMessage(
          `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        )
      }
    },
    [uploadAssets, assets, modalt],
  )

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: <explanation>
    <div
      className="relative min-h-[85vh] md:min-h-[90vh] flex flex-col items-center justify-center pt-24 pb-16 md:pt-32 md:pb-20 px-4 overflow-hidden"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Hero Content Container with brightness boost */}
      <div className="relative z-10 w-full max-w-3xl mx-auto flex flex-col items-center">
        {/* Brightness spotlight on central area - subtle highlight */}
        <div
          className="absolute -inset-32 bg-white/[0.03] rounded-full blur-3xl z-0"
          style={{
            background:
              'radial-gradient(circle, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 50%, transparent 100%)',
          }}
        />
        {/* Backed By OpenAI Fund Badge */}
        <div className="flex justify-center gap-4 mb-8 md:mb-10 animate-fade-in-up">
          <a
            href="https://heyboss.ai/blog/heyboss-ai-seed-funding-openai-startup-fund#the-worlds-first-ai-ceo-just-got-funded"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2.5 px-6 py-3 md:px-8 md:py-4 rounded-full border-2 border-gold/20 bg-white/5 backdrop-blur-sm shadow-[0_0_20px_-5px_rgba(255,215,0,0.2)] hover:bg-white/8 hover:border-gold/30 hover:shadow-[0_0_25px_-5px_rgba(255,215,0,0.3)] transition-all duration-300 group scale-100 hover:scale-105 opacity-80"
          >
            <span className="text-xs md:text-sm font-bold tracking-widest uppercase text-white">
              Backed By
            </span>
            <Image
              src="/homepage/openai-logo-mini.png"
              alt="OpenAI"
              width={120}
              height={32}
              className="h-5 md:h-6 w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity"
            />
            <span className="text-xs md:text-sm font-bold tracking-widest uppercase text-transparent bg-clip-text bg-gold-gradient group-hover:text-white transition-colors">
              Fund
            </span>
          </a>
        </div>
        <div className="text-center mb-12 md:mb-16 animate-fade-in-up [animation-delay:100ms] px-2">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold tracking-tighter text-white/90 leading-[1.1] drop-shadow-lg px-2">
            {t('get_your_website_and_store')}{' '}
            <span className="text-transparent bg-clip-text bg-gold-gradient relative inline-block filter drop-shadow-[0_2px_10px_rgba(255,215,0,0.3)]">
              {t('in_10_minutes')}
              <svg
                className="absolute w-full h-2 -bottom-1 left-0 text-gold opacity-60"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
                role="presentation"
              >
                <path
                  d="M0 5 Q 50 10 100 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
            </span>{' '}
            {t('with_ai')}
          </h1>
        </div>

        <HeroPrompt
          user={user}
          isTemplateMode={false}
          initialAssets={assets}
          onAssetsChange={setAssets}
        />
      </div>

      {/* Full-screen drag overlay */}
      {isDragging && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-50">
          <div className="flex flex-col items-center text-white">
            <Image
              src="/ai-chat/upload.png"
              width={180}
              height={180}
              alt="upload"
              className="mb-7"
              style={{
                animation: 'fastBounce 0.75s ease-in-out infinite',
              }}
            />
            <style jsx>{`
              @keyframes fastBounce {
                0%, 100% {
                  transform: translateY(0);
                  animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
                }
                50% {
                  transform: translateY(-15px);
                  animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
                }
              }
            `}</style>
            <h2 className="text-3xl font-bold mb-4">{t('upload_files')}</h2>
            <p className="text-xl text-gray-300 text-center max-w-md">
              Images, Videos, Audio, ZIP, PDF, CSV only
            </p>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {errorMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[9999] animate-in slide-in-from-top duration-300">
          <div className="bg-gradient-to-br from-[#2F1E30]/95 via-[#3D2A3E] to-[#2F1E30]/95 backdrop-blur-lg rounded-xl px-5 py-4   max-w-lg mx-5 relative">
            <button
              type="button"
              onClick={() => setErrorMessage(null)}
              className="absolute top-2 right-2 text-white/80 hover:text-white transition-colors"
            >
              ×
            </button>
            <div className="flex items-center gap-3 pr-6">
              <div className="w-6 h-6 text-white flex-shrink-0">⚠️</div>
              <div>
                <p className="text-white font-light text-base whitespace-nowrap">
                  {errorMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
